import type { EChartsOption } from 'echarts'
import type { ECharts } from 'echarts/core'
import { SfLog } from '@app/adapters/logger'
import type { GraphData } from '@app/application/report/screens/graphs-echarts/types'
import type { ChartRenderOptions } from '@app/application/report/screens/graphs-echarts/utils/build-chart-option'

export interface EchartsInitOptions extends ChartRenderOptions {
  onSeriesClick?: (params: { seriesName?: string; name?: string }) => void
}

export class EchartsChartManager {
  private chart: ECharts | null = null
  private echartsModule: typeof import('echarts/core') | null = null
  private element: HTMLElement | null = null
  private resizeObserver: ResizeObserver | null = null
  private resizeFrame: number | null = null

  async init(
    element: HTMLElement,
    graphData: GraphData,
    options: EchartsInitOptions = {}
  ): Promise<void> {
    const { onSeriesClick, ...renderOptions } = options
    // Reuse existing chart if already initialized on the same element
    if (this.chart && this.element === element) {
      const option = await this.buildOption(graphData, renderOptions)
      this.chart.setOption(option, true)
      this.attachClickHandler(onSeriesClick)
      return
    }

    this.dispose()
    this.element = element

    try {
      const echarts = await this.loadEcharts()
      const chart = echarts.init(element)
      this.chart = chart
      const option = await this.buildOption(graphData, renderOptions)
      chart.setOption(option)
      this.attachClickHandler(onSeriesClick)

      window.addEventListener('resize', this.handleResize)

      // Container can resize independently of the window — e.g. when the
      // report graphs drawer animates to full viewport width. ECharts only
      // auto-resizes on window resize, so observe the host as well.
      this.resizeObserver = new ResizeObserver(() => this.handleResize())
      this.resizeObserver.observe(element)
    } catch (error) {
      SfLog.error('Failed to initialize ECharts:', { error })
    }
  }

  async update(
    graphData: GraphData,
    options: EchartsInitOptions = {}
  ): Promise<void> {
    const { onSeriesClick, ...renderOptions } = options
    try {
      if (this.chart) {
        const option = await this.buildOption(graphData, renderOptions)
        this.chart.setOption(option, true)
        this.attachClickHandler(onSeriesClick)
      } else if (this.element) {
        await this.init(this.element, graphData, options)
      }
    } catch (error) {
      SfLog.error('Failed to update ECharts:', { error })
    }
  }

  private attachClickHandler(
    onSeriesClick:
      | ((params: { seriesName?: string; name?: string }) => void)
      | undefined
  ): void {
    if (!this.chart) return
    this.chart.off('click')
    if (!onSeriesClick) return

    // echarts' click event hands us the series and category by name; the
    // renderer maps that to report rows (see sf-echarts-renderer's resolveTarget).
    this.chart.on('click', (params) => {
      const p = params as { seriesName?: string; name?: string }
      onSeriesClick({ seriesName: p.seriesName, name: p.name })
    })
  }

  dispose(): void {
    window.removeEventListener('resize', this.handleResize)
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    if (this.resizeFrame !== null) {
      window.cancelAnimationFrame(this.resizeFrame)
      this.resizeFrame = null
    }
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    this.element = null
  }

  private async loadEcharts(): Promise<typeof import('echarts/core')> {
    if (!this.echartsModule) {
      const { echarts } = await import(
        /* webpackChunkName: "echarts" */ '@app/shared/echarts'
      )
      this.echartsModule = echarts
    }
    return this.echartsModule
  }

  private async buildOption(
    graphData: GraphData,
    options: ChartRenderOptions
  ): Promise<EChartsOption> {
    const { buildChartOption } = await import(
      /* webpackChunkName: "echarts" */ '@app/application/report/screens/graphs-echarts/utils/build-chart-option'
    )
    return buildChartOption(graphData, options)
  }

  // Defer to the next frame so the ResizeObserver callback returns before
  // ECharts mutates the DOM. Otherwise the observer can re-fire on the same
  // observation cycle, surfacing a "ResizeObserver loop completed with
  // undelivered notifications" warning.
  private handleResize = (): void => {
    if (this.resizeFrame !== null) return
    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = null
      this.chart?.resize()
    })
  }
}
